import React from 'react';
import { useTranslation } from 'react-i18next';
import './VetoFlow.css';

function mapImgUrl(tag) {
  const ext = tag === 'de_train' ? 'png' : 'jpg';
  return `/stats/images/maps/${tag}.${ext}`;
}

function getMapName(tag, mapPoolInfo) {
  if (mapPoolInfo?.[tag]?.name) return mapPoolInfo[tag].name;
  if (tag.startsWith('de_')) {
    const base = tag.replace('de_', '');
    return base.charAt(0).toUpperCase() + base.slice(1);
  }
  return tag;
}

const WEAPON_LABELS = {
  ak: 'AK-47',
  m4a4: 'M4A4',
  m4a1s: 'M4A1-S',
  deagle: 'Desert Eagle',
  awp: 'AWP',
};

/**
 * Displays veto results including banned/picked maps and weapon mode for duels.
 * Infers game mode from player count when not provided via props.
 */
export default function VetoResults({ vetoState, onContinue, gameMode, weaponMode }) {
  const { t } = useTranslation();
  const { banned_maps, picked_maps, maps_remaining, match_type, knife_round, map_pool_info, team1_name, team2_name, team1_players, team2_players, weapon_mode: vetoWeaponMode } = vetoState;

  // Infer game mode from player count if not provided via props
  const playerCount = (team1_players?.length || 0) + (team2_players?.length || 0);
  const effectiveGameMode = gameMode || (playerCount > 0 && playerCount <= 2 ? 'duel' : null);
  const effectiveWeaponMode = weaponMode || vetoWeaponMode || null;
  const teamName = (tm) => tm === 'team1' ? (team1_name || 'Team 1') : (team2_name || 'Team 2');

  // Build final map list
  // BO1 team_vote: bans down to 2, then pick = the map to play
  // BO1 captain: all bans until 1 remaining = the map to play
  // BO3: picked maps in order + remaining = decider
  let finalMaps;
  if (match_type === 'BO1') {
    if (picked_maps.length > 0) {
      finalMaps = [{ tag: picked_maps[0].map, side: picked_maps[0].side, type: 'picked' }];
    } else {
      finalMaps = maps_remaining.map(m => ({ tag: m, side: knife_round ? 'knife' : null, type: 'decider' }));
    }
  } else {
    finalMaps = [
      ...picked_maps.map(p => ({ tag: p.map, side: p.side, team: p.team, type: 'picked' })),
      ...maps_remaining.map(m => ({ tag: m, side: knife_round ? 'knife' : null, type: 'decider' })),
    ];
  }

  return (
    <div className="veto-results">
      <h2>{t('veto.done')}</h2>

      {/* Banned maps */}
      {banned_maps.length > 0 && (
        <div className="results-section">
          <h3>{t('veto.bannedMaps')}</h3>
          <div className="results-map-list">
            {banned_maps.map((b, i) => (
              <div key={i} className={`results-map banned ${b.team}`}>
                <img className="results-map-img" src={mapImgUrl(b.map)} alt="" />
                <span className="results-map-icon">{'\u2715'}</span>
                <span className="results-map-name">{getMapName(b.map, map_pool_info)}</span>
                <span className="results-map-team">
                  {teamName(b.team)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Picked maps (BO3) */}
      {picked_maps.length > 0 && (
        <div className="results-section">
          <h3>{t('veto.pickedMaps')}</h3>
          <div className="results-map-list">
            {picked_maps.map((p, i) => (
              <div key={i} className={`results-map picked ${p.team}`}>
                <img className="results-map-img" src={mapImgUrl(p.map)} alt="" />
                <span className="results-map-icon">{'\u2713'}</span>
                <span className="results-map-name">{getMapName(p.map, map_pool_info)}</span>
                <span className="results-map-team">
                  {teamName(p.team)}
                  {p.side && ` (${p.side.toUpperCase()})`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Final map(s) to play */}
      <div className="results-section results-final">
        <h3>{match_type === 'BO3' ? t('veto.mapsToPlay') : t('veto.mapToPlay')}</h3>
        <div className="results-map-list">
          {finalMaps.map((m, i) => (
            <div key={i} className={`results-map final ${m.type === 'decider' ? 'decider' : ''}`}>
              <img className="results-map-img" src={mapImgUrl(m.tag)} alt="" />
              {match_type === 'BO3' && <span className="results-map-num">{t('veto.mapNum', { n: i + 1 })}</span>}
              <span className="results-map-name">{getMapName(m.tag, map_pool_info)}</span>
              {m.side && <span className="results-map-side">{m.side.toUpperCase()}</span>}
            </div>
          ))}
        </div>
        {effectiveGameMode === 'duel' && effectiveWeaponMode && (
          <div className="results-weapon-mode">
            {t('veto.weapon')}: {WEAPON_LABELS[effectiveWeaponMode] || effectiveWeaponMode}
          </div>
        )}
      </div>

      <button className="veto-continue-btn" onClick={onContinue}>
        {t('veto.continueBtn')}
      </button>
    </div>
  );
}
